import { Fragment, useState, type ReactNode } from 'react'
import { Box, Button, Dialog, DialogContent, DialogTitle, FormControlLabel, Paper, Stack, Switch, Tooltip, Typography, useMediaQuery } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import LinkIcon from '@mui/icons-material/Link'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import CheckIcon from '@mui/icons-material/Check'
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined'
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import { useTranslation } from 'react-i18next'
import StatusPill from '@/components/researcher/StatusPill'
import StudyQr from '@/components/researcher/StudyQr'
import type { StudyStatus } from '@/components/researcher/dashboardTypes'

type StudyHeaderProps = {
  title: string
  status: StudyStatus
  link: string
  /** Real client name; empty string shows the "internal" fallback. Omit to hide. */
  client?: string
  /** Pre-formatted creation date label. Omit to hide. */
  createdLabel?: string
  /** Study owner (creator) display name. Omit to hide. */
  owner?: string
  onDelete?: () => void
  /** Omit to drop the Export button entirely — FaceWise has no export. */
  onExport?: () => void
  onCopyLink?: () => void
  copied?: boolean
  /** Access code respondents must enter, when the study is gated. Omit/empty to hide the reveal. */
  accessCode?: string
  /** Team sharing. Omit entirely for solo accounts — there is no team to share with. */
  visibility?: 'private' | 'team'
  /** Only the author and the team owner may flip it; others see the state, read-only. */
  canChangeVisibility?: boolean
  onVisibilityChange?: (next: 'private' | 'team') => void
}

/** Study title block: name + status, meta row, and the unique-link bar. */
export default function StudyHeader({
  title,
  status,
  link,
  client,
  createdLabel,
  owner,
  onDelete,
  onExport,
  onCopyLink,
  copied,
  accessCode,
  visibility,
  canChangeVisibility = false,
  onVisibilityChange,
}: StudyHeaderProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useTranslation('monitoring')
  const { t: tTeam } = useTranslation('team')
  const [qrOpen, setQrOpen] = useState(false)
  const [codeShown, setCodeShown] = useState(false)
  const [codeCopied, setCodeCopied] = useState(false)

  const handleCopyCode = async () => {
    if (!accessCode) return
    try {
      await navigator.clipboard.writeText(accessCode)
      setCodeShown(true)
      setCodeCopied(true)
      setTimeout(() => setCodeCopied(false), 1800)
    } catch {
      /* clipboard may be unavailable; revealing the code is enough */
      setCodeShown(true)
    }
  }

  const metaItems = [
    client && (
      <Box component="span" key="client">
        <Box component="span" sx={{ color: c.ink32 }}>
          {t('header.clientLabel')}
        </Box>
        {client}
      </Box>
    ),
    createdLabel && (
      <Box component="span" key="created" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {t('header.createdAt', { date: createdLabel })}
      </Box>
    ),
    owner && (
      <Box component="span" key="owner">
        {owner}
      </Box>
    ),
    visibility !== undefined && (
      <Tooltip
        key="visibility"
        title={tTeam(visibility === 'team' ? 'visibility.hintTeam' : 'visibility.hintPrivate')}
      >
        <FormControlLabel
          sx={{ m: 0, gap: 0.5 }}
          control={
            <Switch
              size="small"
              checked={visibility === 'team'}
              disabled={!canChangeVisibility}
              onChange={(e) => onVisibilityChange?.(e.target.checked ? 'team' : 'private')}
              inputProps={{ 'aria-label': tTeam('visibility.label') }}
            />
          }
          label={
            <Box component="span" sx={{ fontSize: 13, color: c.ink72 }}>
              {tTeam('visibility.label')}
            </Box>
          }
        />
      </Tooltip>
    ),
  ].filter(Boolean) as ReactNode[]

  const ghostBtnSx = {
    color: c.ink72,
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    '&:hover': { bgcolor: c.gray50 },
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: '22px' },
        borderRadius: '18px',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'flex-start' }, gap: 2.25 }}>
        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: theme.affexy.fonts.heading,
                fontWeight: 700,
                fontSize: 26,
                color: c.ink,
                letterSpacing: '-0.01em',
                lineHeight: 1.15,
              }}
            >
              {title}
            </Typography>
            <StatusPill kind={status} />
          </Box>
          {metaItems.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1.75, flexWrap: 'wrap', fontSize: 13, color: c.ink56 }}>
              {metaItems.map((item, i) => (
                <Fragment key={i}>
                  {i > 0 && (
                    <Box component="span" sx={{ color: c.ink32 }}>
                      ·
                    </Box>
                  )}
                  {item}
                </Fragment>
              ))}
            </Box>
          )}
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexShrink: 0, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            disableElevation
            disabled={!link}
            onClick={() => link && window.open(link, '_blank', 'noopener,noreferrer')}
            startIcon={<PlayArrowRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              bgcolor: c.coral,
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 34,
              px: 1.75,
              '&:hover': { bgcolor: alpha(c.coral, 0.88) },
            }}
          >
            {t('header.conduct')}
          </Button>
          <Button
            size="small"
            onClick={onDelete}
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
            sx={{
              bgcolor: alpha(c.danger, 0.08),
              color: c.danger,
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 34,
              px: 1.75,
              '&:hover': { bgcolor: alpha(c.danger, 0.16) },
            }}
          >
            {t('header.delete')}
          </Button>
          {onExport && (
            <Button
              size="small"
              variant="outlined"
              onClick={onExport}
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14, color: c.coral }} />}
              sx={{ fontSize: 13, fontWeight: 600, borderRadius: 34, px: 1.75, borderColor: c.coral, color: c.coral }}
            >
              {t('header.export')}
            </Button>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          px: 1.75,
          py: 1.5,
          bgcolor: c.gray50,
          borderRadius: '12px',
        }}
      >
        <LinkIcon sx={{ fontSize: 16, color: c.ink56 }} />
        <Typography
          sx={{ fontSize: 13, color: c.ink, fontVariantNumeric: 'tabular-nums', flex: '1 1 180px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {link}
        </Typography>
        <Button
          size="small"
          onClick={onCopyLink}
          startIcon={copied ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />}
          sx={ghostBtnSx}
        >
          {copied ? t('header.copied') : t('header.copy')}
        </Button>
        <Button
          size="small"
          onClick={() => setQrOpen(true)}
          startIcon={<QrCode2OutlinedIcon sx={{ fontSize: 13 }} />}
          sx={ghostBtnSx}
        >
          {t('header.qr')}
        </Button>
      </Box>

      {accessCode && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            px: 1.75,
            py: 1.5,
            bgcolor: c.gray50,
            borderRadius: '12px',
          }}
        >
          <VpnKeyOutlinedIcon sx={{ fontSize: 16, color: c.ink56 }} />
          <Typography sx={{ fontSize: 13, color: c.ink56, fontWeight: 600 }}>
            {t('header.accessCodeLabel')}
          </Typography>
          <Typography
            sx={{
              flex: '1 1 120px',
              minWidth: 0,
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: codeShown ? '0.12em' : '0.3em',
              fontVariantNumeric: 'tabular-nums',
              color: codeShown ? c.ink : c.ink56,
            }}
          >
            {codeShown ? accessCode : '•'.repeat(Math.min(accessCode.length, 12))}
          </Typography>
          <Button
            size="small"
            onClick={() => setCodeShown((v) => !v)}
            startIcon={
              codeShown ? (
                <VisibilityOffOutlinedIcon sx={{ fontSize: 14 }} />
              ) : (
                <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
              )
            }
            sx={ghostBtnSx}
          >
            {codeShown ? t('header.hideCode') : t('header.showCode')}
          </Button>
          <Button
            size="small"
            onClick={handleCopyCode}
            startIcon={codeCopied ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />}
            sx={ghostBtnSx}
          >
            {codeCopied ? t('header.codeCopied') : t('header.copy')}
          </Button>
        </Box>
      )}

      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="xs" fullScreen={fullScreen}>
        <DialogTitle sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700 }}>
          {t('header.qr')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, pb: 3 }}>
          <StudyQr value={link} size={220} fileName="study-qr.png" />
          <Typography sx={{ fontSize: 12.5, color: c.ink56, textAlign: 'center', wordBreak: 'break-all', maxWidth: 240 }}>
            {link}
          </Typography>
        </DialogContent>
      </Dialog>
    </Paper>
  )
}
